import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

/**
 * 문서 편집기(ONLYOFFICE) 설정을 서버에서 받아 온다.
 *
 * <h2>이 응답은 캐시하지 않는다</h2>
 * 설정 안에는 <b>수명이 있는 접근 토큰</b>과 <b>파일의 판(version)에 묶인 편집 세션 키</b>가 들어 있다.
 * 캐시해서 옛 설정으로 편집기를 띄우면, 남이 저장해 둔 새 내용이 아니라 <b>옛 내용</b>이 열린다 —
 * 그 상태로 저장하면 남의 편집을 덮어쓴다. 그래서 열 때마다 새로 묻는다.
 */

export type OfficeEditorConfig = {
  enabled: boolean;
  documentServerUrl: string | null;
  /** ONLYOFFICE DocEditor 설정(서명 토큰 포함). 서버가 만든 그대로 넘긴다. */
  config: Record<string, unknown> | null;
  canEdit: boolean;
  /** 문서서버가 아예 없을 때 그 이유. 켜져 있으면 null. */
  reason: string | null;
  /** 편집기는 떴는데 보기 전용인 이유(권한 / 옛 형식). 고칠 수 있으면 null. */
  viewReason: string | null;
  /** 편집 중 자료실로 내려보내는 주기(초). 0 이면 끔. */
  autosaveSeconds: number;
};

/**
 * <b>지금 저장</b> — 편집 중인 내용을 자료실로 내려보내라고 지시한다(문서서버 forcesave).
 *
 * <p>{@code key} 는 편집기가 받은 설정의 값을 <b>그대로</b> 돌려준다. 서버가 다시 계산할 수 없다 —
 * 키의 재료인 ETag 는 저장할 때마다 바뀌는데 편집 세션의 키는 열 때 그대로이기 때문이다.
 *
 * @returns 실제로 저장됐으면 true, <b>고친 것이 없으면 false</b>(오류가 아니다)
 */
export function officeForceSave(token: string, key: string): Promise<boolean> {
  // 어느 파일인지는 토큰이 들고 있다 — 화면이 경로를 다시 실어 보낼 이유가 없다(OfficeFileToken).
  return apiFetch<boolean>(`/v1/office/forcesave?t=${encodeURIComponent(token)}`, {
    method: 'POST',
    body: { key },
  });
}

/**
 * <b>이 화면은 상태를 미리 묻지 않는다.</b> 버튼 문구는 확장자만으로 정해지고(`officeFormats`),
 * 문서서버가 꺼져 있는지는 `/v1/office/config` 응답이 `enabled:false` + 이유로 알려 준다.
 * 목록마다 상태를 한 번 더 물으면, 서버가 꺼진 배포에서 자료실을 열 때마다 쓸모없는 왕복이 하나 는다.
 *
 * <p>운영 진단용으로 `GET /api/v1/office/status` 가 따로 있다(DOC-41 §8.5).
 */
export function useOfficeConfig(
  source: 'S3' | 'NAS',
  ref: string,
  connectionId: string | null,
  /**
   * 대장 엑셀로 들어왔으면 그 대장. 서버가 이 값을 보고 자료실 권한 대신 <b>그 대장의 문</b>으로
   * 판정한다 — [엑셀로 열기]는 지났는데 편집기에서 「접근 권한이 없습니다」·「파일을 찾을 수
   * 없습니다」로 막히던 것을 없앤다(2026-10-01).
   */
  ledger?: { key: string; siteId?: string | null } | null,
) {
  const params = new URLSearchParams({ source, ref });
  if (connectionId) params.set('connectionId', connectionId);
  if (ledger?.key) params.set('ledger', ledger.key);
  if (ledger?.key && ledger.siteId) params.set('ledgerSite', ledger.siteId);
  return useQuery({
    queryKey: ['office-config', source, ref, connectionId, ledger?.key ?? null, ledger?.siteId ?? null],
    queryFn: () => apiFetch<OfficeEditorConfig>(`/v1/office/config?${params.toString()}`),
    enabled: !!ref,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * 편집기 화면 주소. <b>새 창</b>으로 연다 — 목록 위에 모달로 얹으면 편집기가 세로로 눌려 표가
 * 몇 줄밖에 안 보이고, 실수로 뒤를 눌러 닫으면 편집 중이던 창이 사라진다.
 */
export function officeEditorHref(
  source: 'S3' | 'NAS',
  ref: string,
  connectionId?: string | null,
  /**
   * 이 파일이 <b>대장 엑셀</b>이면 그 대장. 둘에 쓴다 — 저장 소식을 다른 탭에 알리고
   * ({@code ledgerSaveBus}), 저장 뒤에 <b>대장 반영이 깨지지 않았는지</b> 서버에 묻는다.
   *
   * <p>자료실의 보통 문서에는 싣지 않는다. 그랬다가는 남의 견적서를 고칠 때마다 열려 있는 모든
   * 화면이 자료를 다시 읽는다.
   */
  ledger?: { key: string; siteId?: string | null } | null,
): string {
  const params = new URLSearchParams({ source, ref });
  if (connectionId) params.set('connectionId', connectionId);
  if (ledger?.key) params.set('ledger', ledger.key);
  // 현장 대장은 현장까지 알아야 「어느 짝인가」가 정해진다(본사 대장은 이 값이 없다).
  if (ledger?.key && ledger.siteId) params.set('ledgerSite', ledger.siteId);
  return `/docs/office?${params.toString()}`;
}

/**
 * 문서 편집기를 새 창으로 띄운다. 파일마다 창을 나누므로 <b>이름을 파일별로</b> 준다 — 같은 이름을
 * 쓰면 두 번째 파일이 첫 번째 창을 덮어써, 고치고 있던 문서가 사라진다.
 */
export function openOfficeWindow(source: 'S3' | 'NAS', ref: string, connectionId?: string | null): void {
  window.open(officeEditorHref(source, ref, connectionId), `bomi-office-${source}-${ref}`, 'noopener,noreferrer');
}
